<script lang="ts" setup>
// Composables

import { AI_NAME } from '~/features-swym/aura/config/ai'

import { EContext } from '~/features-shared/config-manager/interfaces/contexts'

import InputSearch from '~/features-generic/libs/components/input-search.vue'
import type {
  ModalCommunityCreationProps,
  ModalConversationCreationProps,
  ModalNetworkUserAddProps,
} from '~/features-generic/modals/interfaces/modals'
import { useTracker } from '~/features-generic/tracker/composables/tracker'
import { useTranslations } from '~/features-generic/translations/composables/translations'

// Interfaces

// Props
const props = withDefaults(defineProps<{
  /** Current context. Default is EContext.COMMUNITY */
  context?: EContext

  /** is search enabled. Default is undefined */
  enableSearch?: boolean

  /** Constraints on whether an icon should be displayed or not in the panel-header according to context */
  showIconsConstraints?: { [context: string]: boolean }
}>(), {
  context: EContext.COMMUNITY,
  showIconsConstraints: undefined,
})

// Emits
const emit = defineEmits<{
  (e: 'search', q: string): void
}>()
// To Do code will uncomment in 25xFD03
// const isRTCActivated = useFeatureManager('conversation.rtc')
// const sourceType = computed<SectionSource>(() => {
//   if (props.context === EContext.DM) {
//     return isRTCActivated.value ? 'externaldm' : 'dm'
//   }
//   else {
//     return 'community'
//   }
// })

// Composables instantiation
const { $i18n } = useTranslations('threads-list')
const { $i18n: $i18nNavBar } = useTranslations('navigation-bar')
const { trackPageEvent } = useTracker('left-navigation')
const eventBus = useEventBus('swym-app')
// To Do code will uncomment in 25xFD03
// const { $i18n: $i18nLeftPanelHeader } = useTranslations('left-panel-header')
// const {
//   sectionDropdownOptions,
//   isUnreadActive,
//   filterAllUnreadMessages,
// } = useSectionData({ type: sourceType })

// Local interfaces
interface IconProp {
  tooltip: string
  fonticon: string
  handler: () => void
}

const isSearchActive = ref(false)
const search = ref('')

// reset search when the context change
const inputSearchEl = shallowRef()
tryOnBeforeUnmount(whenever(toRef(props, 'context'), () => {
  search.value = ''
  emit('search', '')
  inputSearchEl.value?.closeInput()
}))

/**
 * @method searchPlaceholder set the placeholder of the search of the threadList
 */
const searchPlaceholder = computed(() => {
  switch (props.context) {
    case EContext.AURA:
    case EContext.DM:
      return $i18n('SearchConversations')
    case EContext.CALLS:
      return $i18n('SearchCalls')
    case EContext.RITUALS:
      return $i18n('SearchRituals')
    case EContext.CONTACTS:
      return $i18n('SearchContacts')
    case EContext.NETWORK:
      return $i18n('SearchNetwork')
    default:
      return $i18n('SearchCommunities')
  }
})

const threadName = computed(() => {
  switch (props.context) {
    case EContext.DM:
      return $i18nNavBar('Conversations')
    case EContext.AURA:
      return AI_NAME
    case EContext.CALLS:
      return $i18nNavBar('Calls')
    case EContext.RITUALS:
      return $i18nNavBar('Rituals')
    case EContext.CONTACTS:
      return $i18nNavBar('Contacts')
    case EContext.NETWORK:
      return $i18nNavBar('Network')
    case EContext.ME:
      return $i18nNavBar('Me')
    default:
      return $i18nNavBar('Communities')
  }
})

/**
 * @constant icons next to the title
 */
const icons = computed<IconProp[]>(() => {
  let iconsList: IconProp[] = []
  switch (props.context) {
    case EContext.COMMUNITY:
      if (!props.showIconsConstraints || props.showIconsConstraints[EContext.COMMUNITY]) {
        iconsList = [
          {
            fonticon: 'plus',
            tooltip: $i18n('createCommunity'),
            handler: () => {
              trackPageEvent({
                category: 'ThreadToolbar',
                event: 'click',
                label: 'createCommunity',
                pd: {
                  containerType: 'community',
                },
              })

              const modalProps: ModalCommunityCreationProps = {}

              eventBus.emit('modal-manager:open', {
                component: 'modal-community-creation',
                props: modalProps,
              })
            },
          },
        ]
      }
      return iconsList
    case EContext.AURA:
      return [
        {
          fonticon: 'topbar-chat-add',
          tooltip: $i18n('startConversation'),
          handler: () => {
            trackPageEvent({
              category: 'ThreadToolbar',
              event: 'click',
              label: 'createConversation',
              pd: {
                containerType: 'community',
              },
            })

            eventBus.emit('modal-manager:open', {
              component: 'modal-ai-conversation-creation',
            })
          },
        },
      ]
    case EContext.DM:
      return [
        {
          fonticon: 'topbar-chat-add',
          tooltip: $i18n('startConversation'),
          handler: () => {
            trackPageEvent({
              category: 'ThreadToolbar',
              event: 'click',
              label: 'createConversation',
              pd: {
                containerType: 'community',
              },
            })

            const modalProps: ModalConversationCreationProps = {}

            eventBus.emit('modal-manager:open', {
              component: 'modal-conversation-creation',
              props: modalProps,
            })
          },
        },
      ]
    case EContext.CALLS:
      return []
    case EContext.ME:
      return []
    case EContext.NETWORK:
      return [
        {
          fonticon: 'users-add',
          tooltip: $i18n('addPeopleToNetwork'),
          handler: () => {
            trackPageEvent({
              category: 'ThreadToolbar',
              event: 'click',
              label: 'addUserToMyNetwork',
              pd: {
                context: 'NETWORK',
              },
            })

            const modalProps: ModalNetworkUserAddProps = {}

            eventBus.emit('modal-manager:open', {
              component: 'modal-network-user-add',
              props: modalProps,
            })
          },
        },
      ]
    case EContext.RITUALS:
      return [
        {
          fonticon: 'plus',
          tooltip: $i18n('CreateRitual'),
          handler: () => {
            const modalProps: ModalCommunityCreationProps = {}

            eventBus.emit('modal-manager:open', {
              component: 'modal-community-selector',
              props: modalProps,
            })
          },
        },
      ]
    default:
      return []
  }
})

whenever(isSearchActive, () => {
  trackPageEvent({
    category: 'ThreadToolbar',
    label: 'searchLeftPanel',
    event: 'click',
  })
})
</script>

<template>
  <div
    class="left-panel-header flex items-center px-2.5 sm:px-3.5"
  >
    <transition name="scale">
      <div
        v-show="!isSearchActive"
        class="w-full min-w-[50%] h-full flex items-center"
      >
        <span
          class="thread-name font-bold inline-bloc text-grey-8 text-lg sm:text:xl md:text-2xl md:pl-1.5"
        >
          {{ threadName }}
        </span>
      </div>
    </transition>
    <InputSearch
      v-if="enableSearch"
      ref="inputSearchEl"
      v-model:active="isSearchActive"
      :placeholder="searchPlaceholder"
      @search="search = $event; emit('search', $event)"
    />
    <vu-icon-btn
      v-for="icon in icons"
      :key="`left-panel-header-action-icons-${icon.fonticon}`"
      v-tooltip="icon.tooltip"
      class="text-2xl min-w-[38px] w-[38px]"
      :icon="icon.fonticon"
      @click="icon.handler"
    />
  </div>
  <!--  To Do code will uncomment in 25xFD03 -->
  <!-- <LeftPanelHeader
    :enable-search="enableSearch"
    :icons="icons"
    :search-placeholder="searchPlaceholder"
    :title="threadName"
    @search="search"
  >
    <template #title>
      <vu-badge
        v-if="isUnreadActive"
        class="unread-message-badge"
        closable
        color="warning"
        icon="eye-partial"
        selectable
        @click="filterAllUnreadMessages()"
      >
        {{ $i18nLeftPanelHeader('unread') }}
      </vu-badge>
      <span
        v-else
        class="thread-name font-bold inline-block text-grey-8 text-2xl pl-1.5"
      >
        {{ threadName }}
      </span>
    </template>
    <template #more>
      <vu-dropdownmenu
        v-if="context === EContext.COMMUNITY || context === EContext.DM"
        :items="sectionDropdownOptions"
      >
        <vu-icon-btn
          class="text-2xl min-w-[38px] w-[38px]"
          icon="chevron-down shrink-1 flex justify-center items-center "
        />
      </vu-dropdownmenu>
    </template>
  </LeftPanelHeader> -->
</template>

<style lang="scss" scoped>
.left-panel-header {
  flex: 0 0 auto;
  display: flex;
  background-color: white;
  min-height: 38px;
}
// To Do code will uncomment in 25xFD03
//#app .unread-message-badge{
// @apply rounded-full;
//}
</style>
